import { isNotNull } from "../core/helpers/object-util.js";
import Model from "./Model.js";
import VatPaymentSettingModel from "./forecast/VatPaymentSettingModel.js";
import VatRate from "./VatRate.js";

/**
 * Model holding all the settings related to a country VAT.
 *
 * @param {VatRate[]} vatRates - Array of VatRates
 * @param {VatPaymentSettingModel[]} vatPaymentSettings - Array of VatPaymentSettingModel
 */
export default class VatSettings extends Model {
	constructor({ vatRates = [], vatPaymentSettings } = {}) {
		super(...arguments);
		this._vatRates = vatRates;
		this._vatPaymentSettings = vatPaymentSettings;
	}

	getVatRates() {
		return this._vatRates;
	}

	addRate(rate) {
		this._vatRates.push(rate);
	}

	getDefaultVatRate() {
		return this._vatRates.find((rate) => rate.isDefault());
	}

	setAsDefault({ rate }) {
		this._vatRates.forEach((vatRate) => {
			vatRate.setIsDefault(rate === vatRate);
		});
	}

	removeRateAtIndex(index) {
		this._vatRates.splice(index, 1);
		const hasADefautRate = this._vatRates.some((vatRate) =>
			vatRate.isDefault(),
		);

		if (!hasADefautRate) {
			this._vatRates[0]?.setIsDefault(true);
		}
	}

	getVatPaymentSettings() {
		return this._vatPaymentSettings;
	}

	addVatPaymentSetting(vatPaymentSetting) {
		this._vatPaymentSettings.push(vatPaymentSetting);
	}

	removeVatPaymentSetting(vatPaymentSetting) {
		this._vatPaymentSettings = this._vatPaymentSettings.filter(
			(item) =>
				item.getVatPaymentPeriod() !==
				vatPaymentSetting.getVatPaymentPeriod(),
		);
	}

	getVatPaymentSetting(vatPaymentPeriod) {
		return this._vatPaymentSettings.find(
			(vatPaymentSettingsModel) =>
				vatPaymentSettingsModel.getVatPaymentPeriod() ===
				vatPaymentPeriod,
		);
	}

	containsVatPaymentPeriod(vatPaymentPeriod) {
		return isNotNull(this.getVatPaymentSetting(vatPaymentPeriod));
	}

	clone() {
		const context = {
			vatRates: this._vatRates.map((vatRate) => vatRate.clone()),
			vatPaymentSettings: this._vatPaymentSettings.map(
				(vatPaymentSetting) => vatPaymentSetting.clone(),
			),
		};
		return this.constructor.materialize(this.serialize(), context);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.rates = this._vatRates.map((vatRate) => vatRate.serialize());

		properties.vatPaymentSettings = this._vatPaymentSettings;

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.vatRates = VatRate.materializeAll(data.rates, context);

		data.vatPaymentSettings = VatPaymentSettingModel.materializeAll(
			data.vatPaymentSettings,
			context,
		);
	}
}
